/**
 * Strings of a scripted recipe's script as the designer and the wizard show it
 * (docs/design/ENGINE_DESIGN.md §2.20, engine step E12b); merged into `ca` by
 * src/i18n/ca.ts. Read-only words: nothing here names a control that edits the
 * script, which only the recipe's own options change, by generating again.
 */
export const ca_script = {
  script: {
    panel: 'Guió',
    hint: 'El guió d’aquest tipus de missió, tal com l’ha escrit el generador. Es llegeix i no s’edita: el que el canvia són les opcions pròpies del tipus, que tornen a generar la missió.',
    places: 'Llocs',
    scenes: 'Escenes',
    forces: 'Forces del guió',
    cast: 'Personatges',
    mechanics: 'Mecàniques',
    phases: 'Fases',
    clock: 'Rellotge',
    end: 'Com acaba',
    none: 'Res.',
    /** A place's kind, in words; an unknown one is shown as written. */
    placeKinds: {
      camp: 'Campament',
      qrf_post: 'Lloc de la reacció',
      rv: 'Punt de reunió',
      village: 'Poble',
      outpost: 'Lloc avançat',
      airfield: 'Aeròdrom',
      radar_site: 'Radar',
      landmark: 'Punt de referència',
      keep: 'Reducte',
      village_exit: 'Sortida del poble',
      house: 'Casa',
      destination: 'Destinació',
      checkpoint: 'Control',
      meeting: 'Lloc de la reunió',
      crash_site: 'Lloc de l’accident',
      store: 'Magatzem',
      post: 'Lloc de guàrdia',
      park_camp: 'Campament del parc',
      motorcade: 'Comitiva',
      bench: 'Banc',
      carry: 'Punt de càrrega',
      radar_station: 'Estació de radar',
      farm: 'Casa del pagès',
      workshop: 'Taller',
    } as Record<string, string>,
    /** A place the generator could not name after a kind it knows: still a place, still Catalan. */
    placeUnknown: 'Lloc del guió',
    variants: {
      site: 'instal·lació del mapa',
      villageEdge: 'a la vora d’un poble',
      openGround: 'a camp obert',
      road: 'a la carretera',
      roadCell: 'a la carretera que surt del poble',
      isolatedCell: 'cases aïllades',
      street: 'al carrer',
      junction: 'a la cruïlla',
      fork: 'a la bifurcació',
      bridge: 'al pont',
      bridgeBank: 'a la riba del pont',
      beach: 'a la platja',
      woodEdge: 'a la vora del bosc',
      highGround: 'en un turó',
      summit: 'al cim',
      summitPost: 'al lloc del cim',
      overlook: 'en un mirador',
      tower: 'a la torre',
      bunker: 'al búnquer',
      lz: 'a la zona d’aterratge',
      hemmedRoad: 'a la carretera encaixonada',
      farm: 'a la casa del pagès',
      workshop: 'al taller',
      moved: 'moguda',
      church: 'l’església',
      chapel: 'la capella',
      castle: 'el castell',
      office: 'l’edifici d’oficines',
      military_office: 'l’edifici d’oficines',
      police: 'la comissaria',
      school: 'l’escola',
      barracks: 'la caserna',
      military_hq: 'la caserna',
      prison: 'la presó',
    } as Record<string, string>,
    /**
     * A terrain's map labels that say what a place is, not what it is called
     * (the platform's PlaceLabels): on the map they are said in Catalan, never
     * as the map writes them («Base (military)»).
     */
    mapLabels: {
      military: 'zona militar',
      'military area': 'zona militar',
      'mil.': 'zona militar',
      'military base': 'base militar',
      'training base': 'base d’instrucció',
      'military camp': 'campament militar',
      barracks: 'caserna',
      'military range': 'camp de tir',
      'firing range': 'camp de tir',
      'shooting range': 'camp de tir',
      'tank range': 'camp de tir de carros',
      'vehicle range': 'camp de proves de vehicles',
      checkpoint: 'punt de control',
      outpost: 'lloc avançat',
      'old outpost': 'vell lloc avançat',
      airbase: 'base aèria',
      airfield: 'aeròdrom',
      airstrip: 'aeròdrom',
      airport: 'aeroport',
      terminal: 'terminal',
      factory: 'fàbrica',
      'ind.': 'zona industrial',
      'power plant': 'central elèctrica',
      'diesel power plant': 'central elèctrica',
      quarry: 'pedrera',
      mine: 'mina',
      storage: 'magatzem',
      'fuel depot': 'dipòsit de combustible',
      'railway depot': 'dipòsit ferroviari',
      'train station': 'estació de tren',
      dump: 'abocador',
      sawmill: 'serradora',
      'lumber mill': 'serradora',
      lumberyard: 'serradora',
      farm: 'granja',
      'old fields': 'camps vells',
      plantation: 'plantació',
      'cocoa plantations': 'plantacions de cacau',
      'banana plantations': 'plantacions de bananes',
      'forest clearing': 'clariana',
      harbor: 'port',
      harbour: 'port',
      port: 'port',
      'harbor remnants': 'restes del port',
      ferry: 'transbordador',
      bridge: 'pont',
      castle: 'castell',
      'fortress ruins': 'ruïnes de la fortalesa',
      'temple ruins': 'ruïnes del temple',
      'camp remnants': 'restes del campament',
      stadium: 'estadi',
      'radio station': 'emissora de ràdio',
      'hotel resort': 'complex hoteler',
      roadhouse: 'hostal de carretera',
      'the villa': 'vil·la',
      'big mosque': 'gran mesquita',
      maze: 'laberint',
      'coral reef': 'escull de corall',
    } as Record<string, string>,
    means: { foot: 'a peu', vehicle: 'amb vehicle' } as Record<string, string>,
    radius: (m: number) => `radi ${Math.round(m)} m`,
    byRoad: (m: number) =>
      m >= 1000
        ? `${(m / 1000).toFixed(1).replace('.', ',')} km per carretera`
        : `${m} m per carretera`,
    namedAfter: (place: string) => `de ${place}`,
    sceneForces: (n: number) => (n === 1 ? '1 força' : `${n} forces`),
    sceneAt: (place: string) => `al lloc «${place}»`,
    /** The runtime mechanics by name (engine.hpp's registry), in words. */
    mechanicNames: {
      sleep: 'Son dels que dormen',
      generator: 'Generador i llums',
      nightAlarm: 'Alarma',
      fuelFireball: 'El dipòsit que esclata',
      qrf: 'La reacció',
      radioSetSearch: 'La guàrdia de ràdio',
    } as Record<string, string>,
    mechanicAt: (scene: string) => `a «${scene}»`,
    /** A scripted force's posture (ENGINE_DESIGN.md §2.4). */
    postures: {
      rooted: 'fix al seu lloc',
      walking: 'fent la ronda',
      asleep: 'dormint',
      held: 'en reserva',
      guard: 'de guàrdia',
    } as Record<string, string>,

    /* ---- A phase's trigger, in words. */
    when: {
      start: 'en començar la missió',
      alarm: { quiet: 'sense cap alarma', up: 'amb l’alarma donada' } as Record<string, string>,
      playersWithin: (d: number, of: string) => `un jugador a menys de ${d} m de ${of}`,
      playersAnyBeyond: (d: number, of: string) => `un jugador a més de ${d} m de ${of}`,
      playersAllBeyond: (d: number, of: string) => `tots els jugadors a més de ${d} m de ${of}`,
      event: (name: string) => `quan passa «${name}»`,
      eventQuiet: 'sense alarma',
      eventLoud: 'amb l’alarma donada',
      objective: (title: string, state: string) => `«${title}» ${state}`,
      states: {
        pending: 'pendent',
        active: 'actiu',
        open: 'per fer',
        done: 'assolit',
        failed: 'fallit',
      } as Record<string, string>,
      afterPhase: (seconds: number, phase: string) =>
        `${minutes(seconds)} després de la fase «${phase}»`,
      afterEvent: (seconds: number, event: string) => `${minutes(seconds)} després de «${event}»`,
      sun: (m: number) => `${m} minuts abans de la sortida del sol`,
      count: (n: number, event: string) => `almenys ${n} vegades «${event}»`,
      state: (key: string, how: string) => `«${key}» ${how}`,
      atLeast: (v: unknown) => `com a mínim ${String(v)}`,
      atMost: (v: unknown) => `com a màxim ${String(v)}`,
      equals: (v: unknown) => `igual a ${String(v)}`,
      playersAlive: (how: string) => `jugadors vius: ${how}`,
      clockMark: (mark: string) => `quan arriba la marca «${mark}» del rellotge`,
      unitsWithin: (who: string, d: number, of: string) => `${who} a menys de ${d} m de ${of}`,
      aboard: (who: string, vehicle: string) => `${who} a bord de ${vehicle}`,
      cargo: (of: string, at: string) => `${of} a prop de ${at}`,
      players: 'els jugadors',
      and: ' i ',
      or: ' o ',
      not: (x: string) => `no (${x})`,
      unknown: (key: string) => `«${key}»`,
    },
    /** Where something stands, as a phase names it (scriptTarget). */
    target: {
      base: 'la base',
      insertion: 'la inserció',
      extraction: 'l’extracció',
      scene: (label: string) => `«${label}»`,
      role: (role: string, label: string) => `«${role}» de «${label}»`,
      place: (id: string) => `el lloc «${id}»`,
      force: (label: string) => `la força «${label}»`,
    },

    /* ---- What a phase does, in words. */
    does: {
      radio: (text: string) => `Ràdio: «${text}»`,
      activate: (title: string) => `Obre «${title}»`,
      mechanic: (id: string, mode: string) => `${id}: ${mode}`,
      resolve: (title: string, state: string) => `«${title}» ${state}`,
      release: (forces: string) => `Entren en joc: ${forces}`,
      posture: (who: string, to: string) => `${who} passa a ${to}`,
      marker: (id: string) => `Marcador «${id}»`,
      intel: (id: string) => `Intel «${id}»`,
      hint: (text: string) => `Avís: «${text}»`,
      move: (to: string) => `Es mou a ${to}`,
      action: (id: string) => `Acció «${id}»`,
      emit: (name: string) => `Fa saber «${name}»`,
      clock: (mark: string) => `Rellotge: «${mark}»`,
      delay: (s: number) => `al cap de ${s} s`,
      other: (key: string) => key,
    },
    after: (phases: string) => `Després de: ${phases}`,

    /* ---- The clock and the end. */
    genericClock: (m: number) =>
      `El de sempre: avisa quan falten 30 i 10 minuts dels ${m} previstos, i no acaba la missió.`,
    ownClock: 'El del guió: els avisos genèrics no sonen.',
    mark: (id: string, at: string) => `${id}: ${at}`,
    outcomes: {
      success: 'Missió complerta',
      partial: 'Missió parcialment complerta',
      failed: 'Missió fallida',
    } as Record<string, string>,
    outcomeWhen: (outcome: string, when: string) => `${outcome}: ${when}`,
    genericEnd:
      'Com sempre: complerta quan s’assoleixen els objectius principals, fallida si en falla un.',
    report: (keys: string) => `L’informe final diu: ${keys}`,

    /* ---- The recipe's own options, which generate the mission again. */
    own: 'Opcions pròpies del tipus',
    ownHint:
      'Cada opció torna a generar la missió amb el mateix codi i la mateixa llavor. El que s’hagi editat a mà es perd, i queda a l’historial.',
    ownNone: 'Aquest tipus no té opcions pròpies.',
    /** Release D, D6.3: the type's own switches and choices, each a regeneration. */
    settings: 'Girs opcionals i opcions del tipus',
    settingsHint:
      'Els girs opcionals estan desactivats per defecte i el generador no en sorteja cap. Canviar-ne un torna a generar la missió amb el mateix codi i la mateixa llavor.',
    byDefault: '(per defecte)',
    accepted: 'Girs que admet aquest tipus',
    acceptedHint:
      'Només els girs que el guió accepta, cadascun amb la seva finestra. Afegir-ne o treure’n un torna a generar la missió.',
    window: (words: string) => `Només ${words}.`,
    on: 'Activada',
    off: 'Desactivada',
    regenerating: 'Tornant a generar…',
    regenerated: 'La missió s’ha tornat a generar.',
    regenerateAsk: {
      title: 'Tornar a generar la missió?',
      body: 'La missió es tornarà a generar amb aquest canvi, amb el mateix codi i la mateixa llavor. Els canvis fets a mà es mantenen allà on encara tenen on anar; la revisió actual queda a l’historial.',
    },
    regenerateConfirm: 'Torna a generar',

    /* ---- A stored mission older than the generator's current rules (2026-10-12). */
    stale: {
      title: 'Missió generada amb una versió anterior',
      made: (version: string | null) =>
        version
          ? `Aquesta missió es va generar amb la versió ${version}.`
          : 'Aquesta missió es va generar amb una versió anterior de la plataforma.',
      changed: (version: string | null) =>
        `${version ? `La versió actual (${version})` : 'La versió actual'} ha canviat com es generen les missions, i el que és nou no hi arriba fins que es torni a generar.`,
      regenerate: 'Torna-la a generar',
      ask: {
        title: 'Tornar a generar la missió?',
        body: 'La missió es tornarà a generar tal com es va demanar, amb el mateix codi i la mateixa llavor, amb les regles d’ara. Els canvis fets a mà es mantenen allà on encara tenen on anar; la revisió actual queda a l’historial.',
      },
      done: 'La missió s’ha tornat a generar amb la versió actual.',
      notYours: 'La pot tornar a generar qui la pot editar.',
    },

    /* ---- Moving a place the generator answered (release D, D11.8). */
    move: 'Mou',
    moveAria: (label: string) => `Mou ${label}`,
    points: 'Base i extracció',
    pointNames: {
      'point:base': 'Base de partida',
      'point:extraction': 'Zona d’extracció',
      'point:insertion': 'Punt d’inserció',
    } as Record<string, string>,
    alternative: 'Alternativa: es mou amb el lloc que substitueix.',
    moveTitle: (label: string) => `Mou: ${label}`,
    moveHint:
      'Un lloc del generador només es mou entre els llocs possibles del seu tipus en aquest mapa. Clica’n un: primer es veu què canviaria, i no es desa res fins que ho confirmis.',
    moveLoading: 'Buscant els llocs possibles…',
    moveUnavailable: 'No s’han pogut llegir els llocs possibles d’aquest lloc.',
    moveNone: 'Aquest lloc no té cap altre lloc possible en aquest mapa.',
    moveCount: (n: number, truncated: boolean) =>
      `${n} ${n === 1 ? 'lloc possible' : 'llocs possibles'}${truncated ? ' (els 300 millors)' : ''}. Clica’n un per veure què canviaria.`,
    moveCurrent: 'On és ara',
    movePreviewing: 'Mirant què canviaria…',
    moveSummary: 'Què canviaria',
    moveOutcomes: {
      answered: 'es mou on has triat',
      kept: 'es queda on era',
      reanswered: 'es torna a triar',
      rewritten: 'es torna a escriure',
    } as Record<string, string>,
    moveRow: (label: string, outcome: string, metres: number | null) =>
      `${label}: ${outcome}${metres !== null && metres > 0 ? ` (${metres} m)` : ''}`,
    moveHandEdits:
      'Els canvis fets a mà des de la darrera generació es perdran; la revisió actual queda a l’historial.',
    moveEditsCarried: (n: number) =>
      n === 1
        ? 'Es mantindrà el canvi que has fet a mà.'
        : `Es mantindran els ${n} canvis que has fet a mà.`,
    moveEditDropped: (why: string) => `No es mantindrà un canvi fet a mà: ${why}.`,
    moveTextNames: (name: string) =>
      `Un text que vas escriure esmenta ${name}, que la missió nova ja no fa servir: revisa’l.`,
    movePreflight: (n: number) =>
      n === 1
        ? 'Abans de jugar, la comprovació prèvia en mouria un element.'
        : `Abans de jugar, la comprovació prèvia en mouria ${n} elements.`,
    moveConfirm: 'Confirma',
    moveCancel: 'Cancel·la',
    moved: 'Lloc mogut: la missió s’ha tornat a generar.',

    /* ---- The other panels, for what the script owns. */
    scripted: 'Del guió',
    scriptedForceHint:
      'Aquesta força és del guió: l’escena, la postura i el lloc no s’editen aquí, perquè el guió en depèn. Una força que afegiu vosaltres és una força normal.',
    forceScene: (s: string) => `Escena: ${s}`,
    forcePosture: (p: string) => `Postura: ${p}`,
    forcePost: (p: string) => `Lloc: ${p}`,
    castScene: (s: string) => `Escena: ${s}`,
    castMechanic: (m: string) => `Mecànica: ${m}`,
    gate: (text: string) => `S’obre més tard: ${text}`,
  },
};

function minutes(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const m = Math.round(seconds / 60);
  return m === 1 ? '1 minut' : `${m} minuts`;
}
